import React, { useEffect, useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { useApp } from '@/contexts/AppContext';
import { useDiagramData } from '@/hooks/useDiagramData';
import { useAuth } from '@/contexts/AuthContext';
import { useThreatCat } from '@/contexts/ThreatCatContext';
import { useProject } from '@/contexts/ProjectContext';
import { useWriteBackLock } from '@/hooks/useWriteBackLock';
import { useReviewCommentsIndex } from '@/hooks/useReviewCommentsIndex';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AICompletionDialog } from './AICompletionDialog';
import NoteSectionCard from './notes/NoteSectionCard';
import { Copy, Save } from 'lucide-react';
import { mergeNoteSections } from '@/lib/notesMerger';
import { aiService } from '@/services/aiService';
import { threatcatService } from '@/services/threatcatService';
import { ThreatCatAuthError } from '@/lib/errors';
import ConnectModal from '@/components/threatcat/ConnectModal';
import { toast } from 'sonner';
import type { CompleteNotesResponse } from '@/types/ai';
import type { NoteType } from '@/types/note';

type NoteDrafts = Record<NoteType, string>;
type NoteEditMode = Record<NoteType, boolean>;

const NOTE_SECTIONS: { type: NoteType; label: string; description: string }[] = [
  { type: 'general', label: 'General', description: 'General notes, system description and documentation' },
  { type: 'assumption', label: 'Assumptions', description: 'Assumptions made during threat modeling' },
  { type: 'scope', label: 'In Scope', description: 'Systems and components included in the threat model' },
  { type: 'out_of_scope', label: 'Out of Scope', description: 'Systems and components explicitly excluded' },
];

const EMPTY_DRAFTS: NoteDrafts = { general: '', assumption: '', scope: '', out_of_scope: '' };
const EMPTY_EDIT_MODE: NoteEditMode = { general: false, assumption: false, scope: false, out_of_scope: false };

export default function NotesTab() {
  const {
    currentDiagram, isProject, diagrams, currentDiagramIndex,
    setAppState, notesEditing, setNotesEditing, notes,
    threats, projectInfo,
  } = useApp();
  const { user } = useAuth();
  const { cookie, getCookie, disconnect } = useThreatCat();
  const { detail } = useProject();
  const { isLocked: isWriteBackLocked, message: writeBackLockMessage } = useWriteBackLock();
  const { scopedEntities } = useDiagramData();
  const reviewIndex = useReviewCommentsIndex();

  // Project-mode state
  const [drafts, setDrafts] = useState<NoteDrafts>(EMPTY_DRAFTS);
  const [editMode, setEditMode] = useState<NoteEditMode>(EMPTY_EDIT_MODE);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [connectOpen, setConnectOpen] = useState(false);
  const pendingSaveRef = useRef(false);
  const baselineNotesRef = useRef<NoteDrafts>(EMPTY_DRAFTS);

  // Project-mode per-section AI state
  const [showSystemInfoDialog, setShowSystemInfoDialog] = useState(false);
  const [systemInfoDescription, setSystemInfoDescription] = useState('');
  const [systemInfoDiagramIndex, setSystemInfoDiagramIndex] = useState<number | null>(null);
  const [generatingSystemInfo, setGeneratingSystemInfo] = useState(false);
  const [showAuthConceptDialog, setShowAuthConceptDialog] = useState(false);
  const [authConceptDiagramIndex, setAuthConceptDiagramIndex] = useState<number | null>(null);
  const [generatingAuthConcept, setGeneratingAuthConcept] = useState(false);
  const [sectionAiError, setSectionAiError] = useState<string | null>(null);


  // Single-diagram mode state (legacy)
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [draft, setDraft] = useState<string>(currentDiagram?.notes || '');
  const [copied, setCopied] = useState<boolean>(false);
  const [cursorPos, setCursorPos] = useState<number | null>(null);

  const isEspecial = !!(user && user.roles?.includes('especial'));
  const hasThreatCatWriteAccess = !!detail?.project_version_id && detail.permission !== 'read_only';
  const canSaveToThreatCat = hasThreatCatWriteAccess && !isWriteBackLocked;

  // In single-diagram mode, we need a diagram to render
  if (!isProject && !currentDiagram) return null;

  const Link = ({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
  );
  const components = { a: Link };

  // ─── Project-mode effects ───────────────────────────────────────────────────

  useEffect(() => {
    if (!isProject) return;
    const map = { ...EMPTY_DRAFTS };
    notes.filter((n) => n.diagram_id == null).forEach((n) => { map[n.type] = n.value; });
    baselineNotesRef.current = map;
    setDrafts(map);
  }, [notes, isProject]);

  // ─── Single-diagram mode effects ────────────────────────────────────────────

  useEffect(() => {
    if (isProject) return;
    setDraft(currentDiagram?.notes || '');
    setCursorPos(null);
  }, [currentDiagram, notesEditing, isProject]);

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  const handleUpdateNotesForSelected = (newNotes: string) => {
    if (isProject) {
      const updated = [...diagrams];
      updated[currentDiagramIndex] = { ...updated[currentDiagramIndex], notes: newNotes };
      setAppState({ diagrams: updated });
    } else {
      if (currentDiagram) setAppState({ currentDiagram: { ...currentDiagram, notes: newNotes } });
    }
  };

  const updateDraft = (type: NoteType, value: string) => {
    setDrafts((prev) => ({ ...prev, [type]: value }));
  };

  const toggleEditMode = (type: NoteType) => {
    setEditMode((prev) => ({ ...prev, [type]: !prev[type] }));
  };

  // ─── AI Complete (single-diagram mode only) ───────────────────────────────

  const handleAIComplete = (ai: CompleteNotesResponse) => {
    const merged = mergeNoteSections(currentDiagram?.notes || '', ai as Record<string, string>);
    setDraft(merged);
    handleUpdateNotesForSelected(merged);
    setShowAIDialog(false);
  };

  // ─── Per-section AI: System Information ──────────────────────────────────

  const handleGenerateSystemInfo = async (description: string, diagramIndex: number | null) => {
    if (diagramIndex == null) return;
    const sourceDiagram = diagrams[diagramIndex];
    setShowSystemInfoDialog(false);
    setSystemInfoDescription('');
    setSystemInfoDiagramIndex(null);
    setGeneratingSystemInfo(true);
    setSectionAiError(null);
    try {
      const diagramData = {
        entities: sourceDiagram?.entities ?? [],
        paths: sourceDiagram?.paths ?? [],
        threats: threats.filter((t) => !sourceDiagram || t.diagram_id === sourceDiagram.id || t.diagram_id === null),
        system_description: description,
        project_info: projectInfo ? { name: projectInfo.name, description: projectInfo.description } : undefined,
      };
      const result = await aiService.completeNotes(diagramData, ['system_information']);
      if (result.system_information) {
        setDrafts((prev) => ({
          ...prev,
          general: mergeNoteSections(prev.general, { system_information: result.system_information! }),
        }));
      }
    } catch (err) {
      setSectionAiError(err instanceof Error ? err.message : 'AI generation failed');
    } finally {
      setGeneratingSystemInfo(false);
    }
  };

  // ─── Per-section AI: Auth Concept ────────────────────────────────────────

  const handleGenerateAuthConcept = async (diagramIndex: number | null) => {
    if (diagramIndex == null) return;
    const sourceDiagram = diagrams[diagramIndex];
    setShowAuthConceptDialog(false);
    setAuthConceptDiagramIndex(null);
    setGeneratingAuthConcept(true);
    setSectionAiError(null);
    try {
      const diagramData = {
        entities: sourceDiagram?.entities ?? [],
        paths: sourceDiagram?.paths ?? [],
        threats: threats.filter((t) => !sourceDiagram || t.diagram_id === sourceDiagram.id || t.diagram_id === null),
        system_description: '',
        project_info: projectInfo ? { name: projectInfo.name, description: projectInfo.description } : undefined,
      };
      const result = await aiService.completeNotes(diagramData, ['auth_concept']);
      if (result.auth_concept) {
        setDrafts((prev) => ({
          ...prev,
          assumption: mergeNoteSections(prev.assumption, { auth_concept: result.auth_concept! }),
        }));
      }
    } catch (err) {
      setSectionAiError(err instanceof Error ? err.message : 'AI generation failed');
    } finally {
      setGeneratingAuthConcept(false);
    }
  };

  // ─── Save to ThreatCat ────────────────────────────────────────────────────────

  const executeSaveToThreatCat = async (activeCookie: string) => {
    if (!canSaveToThreatCat || !detail?.project_version_id) return;

    const dirtySections = NOTE_SECTIONS.filter(
      ({ type }) => drafts[type] !== (baselineNotesRef.current[type] ?? '')
    );

    if (dirtySections.length === 0) {
      toast.info('No changes to save');
      return;
    }

    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    const projectVersionId = detail.project_version_id;

    try {
      const existing = (await threatcatService.fetchThreatCatNotes(activeCookie, projectVersionId))
        .filter((n) => n.diagram_id == null);

      for (const { type } of dirtySections) {
        const existingNote = existing.find((n) => n.type === type);
        const newValue = drafts[type];

        if (existingNote?.id != null) {
          if (newValue.trim() === '') {
            await threatcatService.deleteThreatCatNote(activeCookie, projectVersionId, existingNote.id);
          } else if (newValue !== existingNote.value) {
            await threatcatService.updateThreatCatNote(activeCookie, projectVersionId, existingNote.id, type, newValue);
          }
        } else if (newValue.trim() !== '') {
          await threatcatService.createThreatCatNote(activeCookie, projectVersionId, type, newValue);
        }
      }

      const refreshed = await threatcatService.fetchThreatCatNotes(activeCookie, projectVersionId);
      const newBaseline = { ...EMPTY_DRAFTS };
      refreshed.filter((n) => n.diagram_id == null).forEach((n) => { newBaseline[n.type] = n.value; });
      baselineNotesRef.current = newBaseline;
      setAppState({ notes: refreshed });
      setSaveSuccess(true);
      toast.success('Notes saved to ThreatCat');
      window.setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      if (err instanceof ThreatCatAuthError) {
        disconnect();
        return;
      }
      const message = err instanceof Error ? err.message : 'Error saving to ThreatCat';
      setSaveError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveToThreatCat = () => {
    if (!canSaveToThreatCat) return;
    if (!cookie) {
      pendingSaveRef.current = true;
      setConnectOpen(true);
      return;
    }
    void executeSaveToThreatCat(cookie);
  };

  const handleConnected = () => {
    setConnectOpen(false);
    if (!canSaveToThreatCat) {
      pendingSaveRef.current = false;
      return;
    }
    const nextCookie = getCookie();
    if (pendingSaveRef.current && nextCookie) {
      pendingSaveRef.current = false;
      void executeSaveToThreatCat(nextCookie);
    }
  };

  const handleConnectionClose = () => {
    setConnectOpen(false);
    pendingSaveRef.current = false;
  };

  // ─── Scope table helpers ───────────────────────────────────────────────────

  const buildScopeTable = () => {
    let markdown = '| Systems in Scope | Systems out of scope |\n';
    markdown += '|-------------------|----------------------|\n';
    const maxRows = Math.max(scopedEntities.inScope.length, scopedEntities.outOfScope.length);
    for (let i = 0; i < maxRows; i++) {
      markdown += `| ${scopedEntities.inScope[i] || ''} | ${scopedEntities.outOfScope[i] || ''} |\n`;
    }
    return markdown;
  };

  const buildInScopeListForDiagrams = (indices: number[]) => {
    const names = new Set<string>();
    indices.forEach((i) => {
      diagrams[i]?.entities.forEach((e) => { if (e.type !== 'external') names.add(e.name); });
    });
    const sorted = Array.from(names).sort();
    if (sorted.length === 0) return '';
    let md = '| Systems in Scope |\n|------------------|\n';
    sorted.forEach((e) => { md += `| ${e} |\n`; });
    return md;
  };

  const buildOutOfScopeListForDiagrams = (indices: number[]) => {
    const names = new Set<string>();
    indices.forEach((i) => {
      diagrams[i]?.entities.forEach((e) => { if (e.type === 'external') names.add(e.name); });
    });
    const sorted = Array.from(names).sort();
    if (sorted.length === 0) return '';
    let md = '| Systems out of Scope |\n|---------------------|\n';
    sorted.forEach((e) => { md += `| ${e} |\n`; });
    return md;
  };

  // ─── Copy all notes as Markdown (single-diagram mode) ────────────────────────

  const handleCopyAll = async () => {
    const content = notesEditing ? draft : (currentDiagram?.notes || '');
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      console.error('Clipboard write failed', e);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // PROJECT MODE: 4-section layout
  // ─────────────────────────────────────────────────────────────────────────────

  if (isProject) {
    return (
      <div className="space-y-4">
        {/* Toolbar: only Save to ThreatCat */}
        {hasThreatCatWriteAccess && (
          <div className="flex justify-end">
            <span title={writeBackLockMessage ?? undefined} className="inline-flex">
              <Button
                variant="default"
                size="sm"
                onClick={handleSaveToThreatCat}
                disabled={saving || !canSaveToThreatCat}
                title={writeBackLockMessage ?? undefined}
              >
                {saving ? (
                  <>
                    <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent inline-block" />
                    Saving...
                  </>
                ) : saveSuccess ? (
                  'Saved'
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save to ThreatCat
                  </>
                )}
              </Button>
            </span>
          </div>
        )}
        <ConnectModal
          open={connectOpen}
          onClose={handleConnectionClose}
          onConnected={handleConnected}
        />

        {saveError && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3">
            <p className="text-sm text-destructive">{saveError}</p>
          </div>
        )}

        {sectionAiError && (
          <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3">
            <p className="text-sm text-destructive">{sectionAiError}</p>
          </div>
        )}

        {/* 4 note sections */}
        {NOTE_SECTIONS.map(({ type, label }) => (
          <NoteSectionCard
            key={type}
            type={type}
            label={label}
            draft={drafts[type]}
            isEditing={editMode[type]}
            onDraftChange={(v) => updateDraft(type, v)}
            onToggleEdit={() => toggleEditMode(type)}
            isEspecial={isEspecial}
            onAiGenerate={
              type === 'general' ? () => setShowSystemInfoDialog(true)
              : type === 'assumption' ? () => setShowAuthConceptDialog(true)
              : undefined
            }
            isAiGenerating={
              type === 'general' ? generatingSystemInfo
              : type === 'assumption' ? generatingAuthConcept
              : false
            }
            aiLabel={type === 'assumption' ? 'Generating Authentication & Authorization Concept' : undefined}
            diagrams={type === 'scope' || type === 'out_of_scope' ? diagrams : undefined}
            defaultDiagramIndices={
              type === 'scope' ? [currentDiagramIndex]
              : type === 'out_of_scope' ? [currentDiagramIndex]
              : undefined
            }
            scopePickerLabel={
              type === 'scope' ? 'Insert In-Scope'
              : type === 'out_of_scope' ? 'Insert Out-of-Scope'
              : undefined
            }
            onScopeInsert={
              type === 'scope' ? (indices) => {
                const table = buildInScopeListForDiagrams(indices);
                if (table) updateDraft(type, drafts[type] ? `${drafts[type]}\n\n${table}` : table);
              }
              : type === 'out_of_scope' ? (indices) => {
                const table = buildOutOfScopeListForDiagrams(indices);
                if (table) updateDraft(type, drafts[type] ? `${drafts[type]}\n\n${table}` : table);
              }
              : undefined
            }
            reviewComments={reviewIndex.getNoteComments(null, type)}
          />
        ))}

        {/* System Info generation dialog */}
        <Dialog
          open={showSystemInfoDialog}
          onOpenChange={(open) => {
            setShowSystemInfoDialog(open);
            if (!open) setSystemInfoDiagramIndex(null);
          }}
        >
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Generate System Information</DialogTitle>
              <DialogDescription>
                Pick the diagram to base this on and describe the system functionality to help the AI generate an accurate System Information section.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Select
                value={systemInfoDiagramIndex != null ? String(systemInfoDiagramIndex) : undefined}
                onValueChange={(v) => setSystemInfoDiagramIndex(Number(v))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a diagram…" />
                </SelectTrigger>
                <SelectContent>
                  {diagrams.map((d, i) => (
                    <SelectItem key={d.id ?? i} value={String(i)}>
                      {d.name || `Diagram ${i + 1}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Textarea
                value={systemInfoDescription}
                onChange={(e) => setSystemInfoDescription(e.target.value)}
                placeholder="Briefly describe the system's purpose and main functionality..."
                className="min-h-[120px]"
              />
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowSystemInfoDialog(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleGenerateSystemInfo(systemInfoDescription, systemInfoDiagramIndex)}
                disabled={systemInfoDiagramIndex == null}
              >
                Generate
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Auth Concept generation dialog */}
        <Dialog
          open={showAuthConceptDialog}
          onOpenChange={(open) => {
            setShowAuthConceptDialog(open);
            if (!open) setAuthConceptDiagramIndex(null);
          }}
        >
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Generate Authentication & Authorization Concept</DialogTitle>
              <DialogDescription>
                Pick the diagram to base this on.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Select
                value={authConceptDiagramIndex != null ? String(authConceptDiagramIndex) : undefined}
                onValueChange={(v) => setAuthConceptDiagramIndex(Number(v))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a diagram…" />
                </SelectTrigger>
                <SelectContent>
                  {diagrams.map((d, i) => (
                    <SelectItem key={d.id ?? i} value={String(i)}>
                      {d.name || `Diagram ${i + 1}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowAuthConceptDialog(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleGenerateAuthConcept(authConceptDiagramIndex)}
                disabled={authConceptDiagramIndex == null}
              >
                Generate
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // SINGLE DIAGRAM MODE: legacy single-blob layout
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between w-full">
          <CardTitle>Diagram Notes</CardTitle>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (notesEditing) {
                  setNotesEditing(false);
                  handleUpdateNotesForSelected(draft);
                } else {
                  setNotesEditing(true);
                }
              }}
            >
              {notesEditing ? 'Preview' : 'Edit'}
            </Button>
            <div className="flex items-center space-x-3">
              <Button variant="outline" size="sm" onClick={handleCopyAll} disabled={copied}>
                <Copy className="mr-2 h-4 w-4" />
                {copied ? 'Copied' : 'Copy as Markdown'}
              </Button>
              <span className="sr-only" aria-live="polite">{copied ? 'Notes copied to clipboard' : ''}</span>
            </div>
            {isEspecial && notesEditing && (
              <Button variant="default" size="sm" onClick={() => setShowAIDialog(true)}>
                AI Complete
              </Button>
            )}
            {notesEditing && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  const markdown = buildScopeTable();
                  const textarea = document.querySelector('textarea') as HTMLTextAreaElement | null;
                  if (cursorPos !== null) {
                    const before = draft.slice(0, cursorPos);
                    const after = draft.slice(cursorPos);
                    setDraft(before + '\n' + markdown + '\n' + after);
                    requestAnimationFrame(() => {
                      if (textarea) {
                        textarea.selectionStart = textarea.selectionEnd = cursorPos + markdown.length + 2;
                        textarea.focus();
                      }
                    });
                  } else if (textarea && document.activeElement === textarea) {
                    const start = textarea.selectionStart || 0;
                    const end = textarea.selectionEnd || 0;
                    const before = draft.slice(0, start);
                    const after = draft.slice(end);
                    setDraft(before + '\n' + markdown + '\n' + after);
                    requestAnimationFrame(() => {
                      textarea.selectionStart = textarea.selectionEnd = start + markdown.length + 2;
                      textarea.focus();
                    });
                  } else {
                    setDraft((d) => d + '\n' + markdown + '\n');
                  }
                }}
              >
                Insert Scope Table
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {notesEditing ? (
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyUp={(e) => {
              const ta = e.target as HTMLTextAreaElement;
              setCursorPos(ta.selectionStart || 0);
            }}
            onClick={(e) => {
              const ta = e.target as HTMLTextAreaElement;
              setCursorPos(ta.selectionStart || 0);
            }}
            className="min-h-[400px] font-mono text-sm"
            placeholder="Enter your notes here in Markdown format..."
          />
        ) : null}

        {currentDiagram?.notes && currentDiagram.notes.trim() !== '' ? (
          <div className="prose prose-xl dark:prose-invert max-w-none p-2 md:p-4 prose-a:text-blue-600 dark:prose-a:text-blue-400 prose-img:rounded-md prose-img:shadow-sm prose-pre:bg-slate-900 prose-pre:text-slate-100 prose-code:bg-slate-100 dark:prose-code:bg-slate-800 prose-table:rounded-lg prose-td:px-3 prose-th:px-3 prose-hr:border-t prose-hr:my-4">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeSanitize]}
              components={components}
            >
              {currentDiagram?.notes}
            </ReactMarkdown>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No notes available for this diagram.</p>
        )}

        <AICompletionDialog open={showAIDialog} onClose={() => setShowAIDialog(false)} onComplete={handleAIComplete} />
      </CardContent>
    </Card>
  );
}
